import { styled } from '@linaria/react';
import { Badge, Card, Group, Menu, ScrollArea, Stack } from '@mantine/core';
import { useNavigate } from 'react-router';
import {
  useBookmarkOutcomeMutation,
  useHideOutcomeMutation,
  useUnbookmarkOutcomeMutation,
  useUnhideOutcomeMutation,
} from '../../../../api/strategy-client/strategy-client.hooks';
import type { Goal, Initiative, Outcome } from '../../../../api/strategy-client/strategy-client.type';
import { useTranslation } from 'react-i18next';
import { toast } from '../../../../containers/notifications/notifications';
import { useGlobalStore } from '../../../../store/global-store/global-store';
import { secondaryBase, skyDark, skyLight } from '../../../../styles/design-tokens';
import { Icon } from '../../../../ui-library/icon/icon';
import { Loader } from '../../../../ui-library/loader/loader';
import { Tooltip } from '../../../../ui-library/tooltip/tooltip';
import { Text } from '../../../../ui-library/typography/typography';
import { SimplifiedInitiativesTable } from './simplified-initiatives-table';

type Props = {
  outcome: Outcome;
  goals: Goal[];
  initiatives: Initiative[];
};

export const OutcomeOverviewSimplifiedWidget = ({ outcome, goals, initiatives }: Props) => {
  const i18n = useTranslation();
  const navigate = useNavigate();
  const portfolioId = useGlobalStore((state) => state.portfolio?.id);

  const bookmarkMutation = useBookmarkOutcomeMutation();
  const unbookmarkMutation = useUnbookmarkOutcomeMutation();
  const hideMutation = useHideOutcomeMutation();
  const unhideMutation = useUnhideOutcomeMutation();
  const isBookmarkPending = bookmarkMutation.isPending || unbookmarkMutation.isPending;
  const isVisibilityPending = hideMutation.isPending || unhideMutation.isPending;
  const isBookmarked = outcome.is_bookmarked || false;
  const isHidden = outcome.is_hidden || false;

  const handleBookmarkToggle = () => {
    if (isBookmarked) {
      unbookmarkMutation.mutate(outcome.id, {
        onError: () => {
          toast.error(i18n.t('common.bookmark_error'));
        },
      });
    } else {
      bookmarkMutation.mutate(outcome.id, {
        onError: () => {
          toast.error(i18n.t('common.bookmark_error'));
        },
      });
    }
  };

  const handleHide = () => {
    if (!portfolioId) {
      toast.error(i18n.t('common.hide_error'));
      return;
    }
    hideMutation.mutate(
      { outcomeId: outcome.id, portfolioId },
      {
        onError: () => {
          toast.error(i18n.t('common.hide_error'));
        },
      },
    );
  };

  const handleUnhide = () => {
    if (!portfolioId) {
      toast.error(i18n.t('common.unhide_error'));
      return;
    }
    unhideMutation.mutate(
      { outcomeId: outcome.id, portfolioId },
      {
        onError: () => {
          toast.error(i18n.t('common.unhide_error'));
        },
      },
    );
  };

  // Show first goal name + " + X" if more than 1, or just name, or undefined
  const goalNamesDisplay =
    goals.length > 0 ? (goals.length === 1 ? goals[0].name : `${goals[0].name} +${goals.length - 1}`) : undefined;

  return (
    <Card
      shadow="lg"
      radius="md"
      withBorder
      aria-label={isHidden ? `${outcome.name} (${i18n.t('common.hidden')})` : outcome.name}
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '400px',
        opacity: isHidden ? 0.55 : 1,
      }}
      padding="lg"
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          marginBottom: '16px',
        }}
      >
        <Stack gap={5} style={{ flex: 1, minWidth: 0, alignItems: 'flex-start' }}>
          {goalNamesDisplay && (
            <Tooltip
              variant="black"
              label={
                <Stack gap={2}>
                  <Text size="medium" weight="medium">
                    {i18n.t('common.strategic_goals')}
                  </Text>
                  {goals.map((goal) => (
                    <Text key={goal.id}>{goal.name}</Text>
                  ))}
                </Stack>
              }
              zIndex={1000}
            >
              <Text
                weight="medium"
                color={skyDark}
                style={{
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                  width: '100%',
                }}
              >
                {goals.length > 0 ? goalNamesDisplay : i18n.t('strategy.overview.no_associated_goals')}
              </Text>
            </Tooltip>
          )}
          <Group gap={8} align="center" wrap="nowrap" style={{ width: '100%' }}>
            <Tooltip variant="black" label={outcome.name} zIndex={1000}>
              <Text
                size="large"
                weight="medium"
                style={{
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                  flex: 1,
                  minWidth: 0,
                }}
              >
                {outcome.name}
              </Text>
            </Tooltip>
            {isHidden && (
              <Badge variant="light" color="gray" size="xs" radius="sm">
                {i18n.t('common.hidden')}
              </Badge>
            )}
            <Menu shadow="md" width={200} position="bottom-end" zIndex={1000}>
              <Menu.Target>
                <ActionMenuButton>
                  <Icon name="more_horiz" size={18} color={skyDark} style={{ cursor: 'pointer' }} />
                </ActionMenuButton>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Item
                  leftSection={<Icon name="arrow_forward" size={16} color={secondaryBase} />}
                  onClick={() => navigate(`/application/strategy/overview?outcomeId=${outcome.id}`)}
                >
                  <Text size="small">View details</Text>
                </Menu.Item>
                <Menu.Item
                  leftSection={
                    isBookmarkPending ? (
                      <Loader size="xs" />
                    ) : (
                      <Icon
                        name={isBookmarked ? 'bookmark_added' : 'bookmark_add'}
                        size={16}
                        color={isBookmarked ? secondaryBase : skyDark}
                      />
                    )
                  }
                  onClick={handleBookmarkToggle}
                  disabled={isBookmarkPending}
                >
                  <Text size="small">
                    {isBookmarked ? i18n.t('common.remove_bookmark') : i18n.t('common.add_bookmark')}
                  </Text>
                </Menu.Item>
                <Menu.Item
                  leftSection={
                    isVisibilityPending ? (
                      <Loader size="xs" />
                    ) : (
                      <Icon name={isHidden ? 'visibility' : 'visibility_off'} size={16} color={skyDark} />
                    )
                  }
                  onClick={isHidden ? handleUnhide : handleHide}
                  disabled={isVisibilityPending}
                >
                  <Text size="small">{isHidden ? i18n.t('common.unhide') : i18n.t('common.hide')}</Text>
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </Group>
        </Stack>
      </div>

      {/* Scrollable table container */}
      <ScrollArea flex={1} type="hover" scrollbarSize={0} style={{ width: '100%', overflow: 'hidden' }}>
        <SimplifiedInitiativesTable initiatives={initiatives} />
      </ScrollArea>
    </Card>
  );
};

const ActionMenuButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  background: transparent;
  border: 1px solid ${skyLight};
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.2s ease;
  width: 28px;
  height: 28px;

  &:hover {
    background-color: rgba(0, 0, 0, 0.05);
    border-color: ${secondaryBase};
  }
`;
